// DeviceSearchResultsPage.jsx
"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InventoryLayout from "../../components/inventory/modules/Layout/InventoryLayout";
import Card from "../../components/inventory/modules/Card/Card";
import Table from "../../components/inventory/modules/table/table";
import Badge from "../../components/inventory/modules/Badge/Badge";
import Spinner from "../../components/spinner";
import Button from "../../components/inventory/modules/Button/Button"; // <- added
// NOTE: devicesService removed here on purpose (mock data used)
import styles from "./InventoryDashboardPage.module.css"; // reuse styling
import { parseDateSafe } from "../../utils/dateFormatters";

// small helper to parse query params
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const formatDateTime = (iso) => {
  if (!iso) return "N/A";
  const d = parseDateSafe(iso);
  if (!d) return "N/A";
  return d.toLocaleString();
};

const showOrNA = (val) => {
  if (val === null || val === undefined || (typeof val === "string" && val.trim() === "")) return "N/A";
  return val;
};

/**
 * Simple mock generator for device-like objects.
 * Keeps ids stable for the same imei query by using a seeded-like hash.
 */
const makeMockDevices = (searchImei, count = 3) => {
  let seed = 0;
  for (let i = 0; i < (searchImei || "").length; i++) {
    seed = (seed * 31 + searchImei.charCodeAt(i)) >>> 0;
  }
  if (seed === 0) seed = Math.floor(Math.random() * 1000000);

  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) % 0xffffffff;
    return seed / 0xffffffff;
  };

  const types = ["Glucometer", "BP Monitor", "Pulse Oximeter", "Thermometer"];
  const locations = ["Karachi Warehouse", "Lahore Hub", "Islamabad Clinic", "Rawalpindi Depot"];
  const users = ["Ali", "Sara", "Dan", "Nadia", "Omar"];

  const makeImei = (idx) => (searchImei ? `${searchImei}-${idx + 1}` : `MOCK-${Math.floor(rnd() * 900000 + 100000)}`);

  const now = Date.now();

  const items = new Array(count).fill(0).map((_, i) => {
    const id = Math.floor(rnd() * 900000 + 1000) + i;
    const imeiVal = makeImei(i);
    const deviceType = types[Math.floor(rnd() * types.length)];
    const location = locations[Math.floor(rnd() * locations.length)];
    const addedBy = users[Math.floor(rnd() * users.length)];
    const addedAt = new Date(now - Math.floor(rnd() * 1000 * 60 * 60 * 24 * 365)).toISOString();
    const statusChoice = rnd();
    const status = statusChoice > 0.7 ? "assigned" : statusChoice > 0.35 ? "active" : "unlinked";

    return {
      id,
      imei: imeiVal,
      name: `${deviceType} ${Math.floor(rnd() * 1000)}`,
      device_type: deviceType,
      location,
      added_by: addedBy,
      added_at: addedAt,
      status,
    };
  });

  return items;
};

const DeviceSearchResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const initialImei = query.get("imei") || "";
  const [imei, setImei] = useState(initialImei);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  // columns reused from DeviceDistribution page (activeDevicesColumns)
  const activeDevicesColumns = useMemo(
    () => [
      { key: "id", label: "ID", alignLeft: true },
      {
        key: "imei",
        label: "IMEI",
        alignLeft: true,
        render: (row) => showOrNA(row?.imei),
      },
      {
        key: "name",
        label: "Name",
        alignLeft: true,
        render: (row) => showOrNA(row?.name),
      },
      {
        key: "device_type",
        label: "Device Type",
        alignLeft: true,
        render: (row) => showOrNA(row?.device_type),
      },
      {
        key: "location",
        label: "Location",
        alignLeft: true,
        render: (row) => showOrNA(row?.location),
      },
      {
        key: "added_by",
        label: "Added By",
        alignLeft: true,
        render: (row) => (row?.added_by ? showOrNA(row.added_by) : "N/A"),
      },
      {
        key: "added_at",
        label: "Added At",
        alignLeft: true,
        render: (row) => formatDateTime(row?.added_at),
      },
      {
        key: "status",
        label: "Status",
        alignLeft: true,
        render: (row) => {
          const s = (row?.status || "").toString().toLowerCase();
          const b =
            s === "assigned" || s === "available" || s === "active"
              ? { label: "Assigned", variant: "success" }
              : s === "unlinked" || s === "inactive" || s === "deactivated"
              ? { label: "Deactivated", variant: "error" }
              : { label: showOrNA(row?.status), variant: "default" };
          return <Badge variant={b.variant}>{b.label}</Badge>;
        },
      },
    ],
    []
  );

  // MOCKED fetch function: returns deterministic mock devices
  const fetchByImei = useCallback(
    async (searchImei) => {
      if (!searchImei) {
        setResults([]);
        setMeta(null);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        await new Promise((r) => setTimeout(r, 400));
        const count = Math.min(6, Math.max(1, Math.floor((searchImei || "").length / 2) + 1));
        const items = makeMockDevices(searchImei, count);
        setResults(items);
        setMeta({
          total_items: items.length,
          total_pages: 1,
          current_page: 1,
          next: null,
          previous: null,
        });
      } catch (err) {
        console.error("Mock device fetch failed:", err);
        setResults([]);
        setMeta(null);
        setError("Failed to fetch mock devices");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (initialImei) fetchByImei(initialImei);
    setImei(initialImei);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialImei]);

  const handleTopbarSearch = (val) => {
    const q = (val || "").trim();
    setImei(q);
    navigate(`/inventory/device-search?imei=${encodeURIComponent(q)}`);
    fetchByImei(q);
  };

  // BACK button: prefer location.state.from, else history -1, else fallback to /inventory
  const handleBack = () => {
    const fromState = location.state && location.state.from;
    if (fromState && typeof fromState === "string") {
      navigate(fromState);
      return;
    }
    try {
      // if there is a real browser history to go back to, use it
      if (window.history && window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch (e) {
      // ignore and fallback
    }
    navigate("/inventory");
  };

  return (
    <div style={{ paddingTop: 18 }}>
     

      <Card title={`Search — IMEI: ${imei || "none"}`} className={styles.inventory_card} headerRight={
             <Button size="small" variant="primary" onClick={handleBack}>
             Back
          </Button>
          }>
        
          <Table
            columns={activeDevicesColumns}
            data={results}
            meta={meta}
            pagination={false}
            height={480}
            isRowClickable={true}
            loading={loading}
            onRowClick={(row) => {
              const deviceId = row?.id || row?.original?.id;
              if (deviceId) navigate(`/inventory/devices/${deviceId}`);
            }}
          />
       
      </Card>
    </div>
  );
};

export default DeviceSearchResultsPage;
